import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { ref, get } from 'firebase/database';

function Stdfeenamelist() {
  const { className, division } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { studentData, academicYear, feeType, filterCriteria } = state || {};
  const udiseNumber = localStorage.getItem('udiseNumber');
  const [studentFeeData, setStudentFeeData] = useState({});

  // Fetch studentFee data
  useEffect(() => {
    if (!udiseNumber || !academicYear) return;
    const url = ref(db, `schoolRegister/${udiseNumber}/studentFee`);
    get(url)
      .then((snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          if (data && data[academicYear] && typeof data[academicYear] === 'object') {
            setStudentFeeData(data[academicYear]);
          } else {
            setStudentFeeData({});
          }
        }
      })
      .catch((err) => {
        console.error('Error fetching student fee data:', err);
      });
  }, [udiseNumber, academicYear]);

  // Filter students based on the filter criteria
  const studentsForClassAndDivision = studentData
    ? Object.entries(studentData).filter(([serialNo, student]) => {
        // Basic student filter (class, division, active)
        const isMatchingStudent =
          student?.currentClass?.toLowerCase().trim() === className?.toLowerCase().trim() &&
          student?.division?.toLowerCase().trim() === division?.toLowerCase().trim() &&
          student?.isActive !== false;

        if (!isMatchingStudent) return false;

        // Handle pending fees with serial numbers
        if (filterCriteria?.isAdditionalFee && filterCriteria?.serialNo) {
          return serialNo === filterCriteria.serialNo;
        }

        // Handle bus fee students
        if (filterCriteria?.isBusFee) {
          return student?.busFee === true;
        }

        // Handle hostel fee students
        if (filterCriteria?.isHostelFee) {
          return student?.hostel === true;
        }

        // For regular fees, check if they have this fee type
        if (!feeType || !studentFeeData[serialNo]) return true;

        const invoices = studentFeeData[serialNo];
        const lastInvoiceNo =
          invoices?.lastInvoiceNo ||
          Math.max(...Object.keys(invoices).filter((k) => k !== 'lastInvoiceNo').map(Number));

        if (lastInvoiceNo && invoices?.[lastInvoiceNo]) {
          return Object.keys(invoices[lastInvoiceNo]).some(
            (key) => key.toLowerCase().trim() === feeType.toLowerCase().trim()
          );
        }
        return false;
      })
    : [];

  // Function to get fee details for a specific student and feeType
  const getFeeDetails = (serialNo) => {
    if (!studentFeeData[serialNo]) {
      return {
        totalFee: '0',
        discountFee: '0',
        finalAmount: '0',
        receivedFee: '0',
        pendingFee: '0',
      };
    }

    const invoices = studentFeeData[serialNo];
    const lastInvoiceNo =
      invoices.lastInvoiceNo ||
      Math.max(...Object.keys(invoices).filter((k) => k !== 'lastInvoiceNo').map(Number));

    if (lastInvoiceNo && invoices[lastInvoiceNo]) {
      // For pending fees or additional fees, look for the exact match
      if (filterCriteria?.isAdditionalFee) {
        const feeTypeKey = Object.keys(invoices[lastInvoiceNo]).find(
          key => key.toLowerCase().includes('pending') || key.toLowerCase().includes(feeType.toLowerCase())
        );
        
        if (feeTypeKey) {
          const feeDetails = invoices[lastInvoiceNo][feeTypeKey];
          const totalFee = parseFloat(feeDetails.amount || 0);
          const discountFee = parseFloat(feeDetails.discountAmount || 0);
          const receivedFee = parseFloat(feeDetails.beforePay || 0);
          const finalAmount = totalFee - discountFee;
          const pendingFee = finalAmount - receivedFee;

          return {
            totalFee: totalFee.toFixed(2),
            discountFee: discountFee.toFixed(2),
            finalAmount: finalAmount.toFixed(2),
            receivedFee: receivedFee.toFixed(2),
            pendingFee: pendingFee.toFixed(2),
          };
        }
      }
      
      // For bus fee
      if (filterCriteria?.isBusFee) {
        const busFeeKey = Object.keys(invoices[lastInvoiceNo]).find(
          key => key.toLowerCase().includes('bus')
        );
        
        if (busFeeKey) {
          const feeDetails = invoices[lastInvoiceNo][busFeeKey];
          const totalFee = parseFloat(feeDetails.amount || 0);
          const discountFee = parseFloat(feeDetails.discountAmount || 0);
          const receivedFee = parseFloat(feeDetails.beforePay || 0);
          const finalAmount = totalFee - discountFee;
          const pendingFee = finalAmount - receivedFee;

          return {
            totalFee: totalFee.toFixed(2),
            discountFee: discountFee.toFixed(2),
            finalAmount: finalAmount.toFixed(2),
            receivedFee: receivedFee.toFixed(2),
            pendingFee: pendingFee.toFixed(2),
          };
        }
      }
      
      // For hostel fee
      if (filterCriteria?.isHostelFee) {
        const hostelFeeKey = Object.keys(invoices[lastInvoiceNo]).find(
          key => key.toLowerCase().includes('hostel')
        );
        
        if (hostelFeeKey) {
          const feeDetails = invoices[lastInvoiceNo][hostelFeeKey];
          const totalFee = parseFloat(feeDetails.amount || 0);
          const discountFee = parseFloat(feeDetails.discountAmount || 0);
          const receivedFee = parseFloat(feeDetails.beforePay || 0);
          const finalAmount = totalFee - discountFee;
          const pendingFee = finalAmount - receivedFee;

          return {
            totalFee: totalFee.toFixed(2),
            discountFee: discountFee.toFixed(2),
            finalAmount: finalAmount.toFixed(2),
            receivedFee: receivedFee.toFixed(2),
            pendingFee: pendingFee.toFixed(2),
          };
        }
      }
      
      // For regular fee types
      const feeTypeKey = Object.keys(invoices[lastInvoiceNo]).find(
        key => key.toLowerCase().trim() === feeType.toLowerCase().trim()
      );
      
      if (feeTypeKey) {
        const feeDetails = invoices[lastInvoiceNo][feeTypeKey];
        const totalFee = parseFloat(feeDetails.amount || 0);
        const discountFee = parseFloat(feeDetails.discountAmount || 0);
        const receivedFee = parseFloat(feeDetails.beforePay || 0);
        const finalAmount = totalFee - discountFee;
        const pendingFee = finalAmount - receivedFee;

        return {
          totalFee: totalFee.toFixed(2),
          discountFee: discountFee.toFixed(2),
          finalAmount: finalAmount.toFixed(2),
          receivedFee: receivedFee.toFixed(2),
          pendingFee: pendingFee.toFixed(2),
        };
      }
    }

    return {
      totalFee: '0',
      discountFee: '0',
      finalAmount: '0',
      receivedFee: '0',
      pendingFee: '0',
    };
  };

  const handleSerialNoClick = (serialNo) => {
    navigate(`/studentdetails/${className}/${division}/${serialNo}`, {
      state: { studentData, academicYear, feeType },
    });
  };

  const handleClose = () => {
    navigate(-1);
  };

  return (
    <div className='main-content-of-page'>
      <div
        style={{
          padding: '24px',
          maxWidth: '1280px',
          margin: '0 auto',
          background: 'linear-gradient(to bottom right, rgba(0, 123, 255, 0.3), rgba(255, 255, 255, 1))',
          minHeight: '100vh',
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <h1
          style={{
            fontSize: '22px',
            fontWeight: '700',
            color: '#1a202c',
            marginBottom: '24px',
            borderBottom: '2px solid #e2e8f0',
            paddingBottom: '8px',
            textAlign:'center',
          }}
        >
          <button 
            style={{ backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '20px', float: 'right', marginBottom: '10px' }} 
            onClick={handleClose}
          >
            X
          </button>

          Student List for {className} - Division {division} {feeType ? `(${feeType})` : ''}
        </h1>

        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}
        >
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
              }}
            >
              <thead
                style={{
                  backgroundColor: '#edf2f7',
                  textTransform: 'uppercase',
                  color: '#4a5568',
                }}
              >
                <tr>
                  <th
                    style={{
                      padding: '16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor:'#004d99',
                      color:'white'
                    }}
                  >
                    Student Name
                  </th>
                  <th
                    style={{
                      padding: '16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor:'#004d99',
                      color:'white'
                    }}
                  >
                    Fee Type
                  </th>
                  <th
                    style={{
                      padding: '16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor:'#004d99',
                      color:'white'
                    }}
                  >
                    Total Fee
                  </th>
                  <th
                    style={{
                      padding: '16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor:'#004d99',
                      color:'white'
                    }}
                  >
                    Discount Fee
                  </th>
                  <th
                    style={{
                      padding: '16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor:'#004d99',
                      color:'white'
                    }}
                  >
                    Final Amount
                  </th>
                  <th
                    style={{
                      padding: '16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor:'#004d99',
                      color:'white'
                    }}
                  >
                    Received Fee
                  </th>
                  <th
                    style={{
                      padding: '16px',
                      textAlign: 'left',
                      fontWeight: '600',
                      borderBottom: '1px solid #e2e8f0',
                      backgroundColor:'#004d99',
                      color:'white'
                    }}
                  >
                    Pending Fee
                  </th>
                </tr>
              </thead>
              <tbody>
                {studentsForClassAndDivision.length > 0 ? (
                  studentsForClassAndDivision.map(([serialNo, student]) => {
                    const feeDetails = getFeeDetails(serialNo);
                    return (
                      <tr
                        key={serialNo}
                        style={{
                          backgroundColor: '#ffffff',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = '#edf2f7')
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = '#ffffff')
                        }
                      >
                        <td
                          style={{
                            padding: '16px',
                            color: '#2d3748',
                            borderBottom: '1px solid #edf2f7',
                          }}
                        >
                          {`${student.stdName || 'N/A'} ${student.stdFather || ''} ${student.stdSurname || ''}`.trim()}
                        </td>
                        <td
                          style={{
                            padding: '16px',
                            color: '#2d3748',
                            borderBottom: '1px solid #edf2f7',
                          }}
                        >
                          {feeType || (filterCriteria?.isBusFee ? 'Bus Fee' : 
                            filterCriteria?.isHostelFee ? 'Hostel Fee' : 
                            filterCriteria?.isAdditionalFee ? 'Pending Fee' : 'N/A')}
                        </td>
                        <td
                          style={{
                            padding: '16px',
                            color: '#2d3748',
                            borderBottom: '1px solid #edf2f7',
                          }}
                        >
                          {feeDetails.totalFee}
                        </td>
                        <td
                          style={{
                            padding: '16px',
                            color: '#2d3748',
                            borderBottom: '1px solid #edf2f7',
                          }}
                        >
                          {feeDetails.discountFee}
                        </td>
                        <td
                          style={{
                            padding: '16px',
                            color: '#2d3748',
                            borderBottom: '1px solid #edf2f7',
                          }}
                        >
                          {feeDetails.finalAmount}
                        </td>
                        <td
                          style={{
                            padding: '16px',
                            color: '#2d3748',
                            borderBottom: '1px solid #edf2f7',
                          }}
                        >
                          {feeDetails.receivedFee}
                        </td>
                        <td
                          style={{
                            padding: '16px',
                            color: feeDetails.pendingFee > 0 ? '#e53e3e' : '#2d3748',
                            borderBottom: '1px solid #edf2f7',
                            fontWeight: feeDetails.pendingFee > 0 ? '600' : '400',
                          }}
                        >
                          {feeDetails.pendingFee}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        padding: '16px',
                        textAlign: 'center',
                        color: '#2d3748',
                        borderBottom: '1px solid #edf2f7',
                      }}
                    >
                      No students found for {className} - Division {division} {feeType ? `(${feeType})` : ''}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Stdfeenamelist;